<div class="community-list container-xl py-3" *transloco="let t">
  <header class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
    <h1 class="h3 mb-0">{{ t('community.title') }}</h1>
    <div class="d-flex gap-2 flex-wrap">
      <form class="community-search-box d-flex" (ngSubmit)="submitSearch()">
        <input type="search" class="form-control form-control-sm" name="q"
               [placeholder]="t('community.search.placeholder')"
               [(ngModel)]="searchQuery" />
        <button type="submit" class="btn btn-sm btn-outline-secondary ms-1" [disabled]="!searchQuery.trim()">
          🔎
        </button>
      </form>
      @if (canManage()) {
        <a class="btn btn-outline-secondary" [routerLink]="adminLink()">
          {{ t('community.admin.button') }}
        </a>
      }
      @if (isAdmin()) {
        <button type="button" class="btn btn-outline-secondary" (click)="openManage()">
          {{ t('community.manage.button') }}
        </button>
      }
      @if (!createOpen() && canCompose()) {
        <button type="button" class="btn btn-primary" (click)="openCreate()">
          + {{ t('community.threads.new') }}
        </button>
      }
    </div>
  </header>

  @if (overviewError(); as key) {
    <!-- Blocking: the backend refused the network itself (not a member, e-mail unconfirmed). -->
    <div class="alert alert-warning">{{ t(key) }}</div>
  } @else if (overviewLoading() && !overview()) {
    <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
  } @else {
    @if (muted()) {
      <div class="alert alert-warning">{{ t('community.users.muted_notice') }}</div>
    }
    <app-community-sanction-notice [sanctions]="sanctions()" [warnings]="warnings()" />

    <!-- Small screens: the personal entries as buttons, one select instead of the group column. -->
    <div class="d-md-none mb-2 d-flex flex-wrap gap-2">
      <a class="btn btn-sm btn-outline-secondary" [routerLink]="messagesLink()">
        ✉️ {{ t('community.messages.title') }}
        @if (unreadMessages() > 0) { <span class="badge bg-danger ms-1">{{ unreadMessages() }}</span> }
      </a>
      @if (canModerate()) {
        <a class="btn btn-sm btn-outline-secondary" [routerLink]="moderationLink()">🛡 {{ t('community.moderation.title') }}</a>
      }
    </div>
    <div class="d-md-none mb-3">
      <label class="form-label small text-muted">{{ t('community.nav.select_channel') }}</label>
      <select class="form-select" [ngModel]="channelId() ?? ''" (ngModelChange)="onMobileSelect($event)">
        <option value="">{{ t('community.nav.all') }}</option>
        @for (group of groups(); track group.id) {
          @if (!group.locked && group.channels.length) {
            <optgroup [label]="group.name">
              @for (ch of group.channels; track ch.id) {
                <option [value]="ch.id">@if (ch.private) {🔐 }{{ ch.name }}@if (channelBadge(ch); as badge) { ({{ badge.text }})}</option>
              }
            </optgroup>
          }
        }
      </select>
    </div>

    <div class="row g-3">
      <!-- Group column (md+) -->
      <aside class="col-md-3 d-none d-md-block">
        <nav class="community-nav">
          <a class="community-nav__link community-nav__link--all"
             [routerLink]="allLink()"
             routerLinkActive="active"
             [routerLinkActiveOptions]="{ exact: true }">
            {{ t('community.nav.all') }}
          </a>

          <!-- Personal entries: private messages, and moderation for those who hold it. -->
          <section class="community-nav__group community-nav__group--personal">
            <ul class="community-nav__channels">
              <li>
                <a class="community-nav__link" [routerLink]="messagesLink()" routerLinkActive="active">
                  <span class="community-nav__channel-name">✉️ {{ t('community.messages.title') }}</span>
                  @if (unreadMessages() > 0) {
                    <span class="badge bg-danger ms-auto" [title]="t('community.messages.unread_hint')">{{ unreadMessages() }}</span>
                  }
                </a>
              </li>
              @if (canModerate()) {
                <li>
                  <a class="community-nav__link" [routerLink]="moderationLink()" routerLinkActive="active">
                    <span class="community-nav__channel-name">🛡 {{ t('community.moderation.title') }}</span>
                  </a>
                </li>
              }
              <li>
                <a class="community-nav__link" [routerLink]="notificationSettingsLink()" routerLinkActive="active">
                  <span class="community-nav__channel-name">🔔 {{ t('community.settings.title') }}</span>
                </a>
              </li>
            </ul>
          </section>

          @for (group of groups(); track group.id) {
            <section class="community-nav__group" [class.community-nav__group--locked]="group.locked">
              <h2 class="community-nav__group-title">
                @if (iconName(group.icon); as icon) {
                  <svg cIcon [name]="icon" size="sm" class="me-1"></svg>
                }
                {{ group.name }}
                @if (group.locked) {
                  <span class="ms-1" [title]="t('community.nav.locked')" aria-hidden="true">🔒</span>
                }
              </h2>
              @if (group.locked) {
                <p class="community-nav__locked-hint small text-muted mb-1">
                  {{ group.locked_reason === 'health_club_membership'
                      ? t('community.nav.locked_health_club')
                      : t('community.nav.locked_role') }}
                </p>
                @if (group.locked_reason === 'health_club_membership' && purchaseUrl()) {
                  <a class="btn btn-sm btn-outline-primary mb-2"
                     [href]="purchaseUrl()"
                     target="_blank"
                     rel="noopener noreferrer">
                    {{ t('community.nav.purchase') }}
                  </a>
                }
                <ul class="community-nav__channels community-nav__channels--locked">
                  @for (ch of group.channels; track ch.id) {
                    <li class="community-nav__channel text-muted"># {{ ch.name }}</li>
                  }
                </ul>
              } @else {
                <ul class="community-nav__channels">
                  @for (ch of group.channels; track ch.id) {
                    <li>
                      <a class="community-nav__link"
                         [routerLink]="channelLink(ch.id)"
                         routerLinkActive="active"
                         [class.community-nav__link--archived]="ch.archived">
                        <span class="community-nav__channel-name">
                          # {{ ch.name }}
                          @if (ch.read_only && !ch.archived) {
                            <span class="ms-1" [title]="t('community.channel.read_only')" aria-hidden="true">🔏</span>
                          }
                          @if (ch.private) {
                            <span class="ms-1" [title]="t('community.private_room.badge')" aria-hidden="true">🔐</span>
                            <span class="visually-hidden">{{ t('community.private_room.badge') }}</span>
                          }
                        </span>
                        @if (channelBadge(ch); as badge) {
                          <span class="badge ms-auto"
                                [class.bg-danger]="badge.unread"
                                [class.bg-light]="!badge.unread"
                                [class.text-dark]="!badge.unread"
                                [title]="badge.unread ? t('community.nav.unread_count') : t('community.nav.thread_count')">{{ badge.text }}</span>
                        }
                      </a>
                    </li>
                  }
                </ul>
              }
            </section>
          }

          <!-- Self-service roles ("I am interested in sales"). -->
          @for (role of optInRoles(); track role.key) {
            <div class="card community-nav__optin mt-3">
              <div class="card-body p-3">
                <div class="fw-semibold mb-1">{{ role.name }}</div>
                @if (optInDescription(role); as description) {
                  <p class="small text-muted mb-2">{{ description }}</p>
                }
                <button type="button" class="btn btn-sm btn-primary"
                        [disabled]="optInPending() === role.key"
                        (click)="optIn(role)">
                  @if (optInPending() === role.key) {
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                  }
                  {{ t('community.opt_in.join') }}
                </button>
              </div>
            </div>
          }
        </nav>
      </aside>

      <!-- Thread column -->
      <div class="col-md-9">
        @if (actionError(); as key) {
          <div class="alert alert-danger d-flex justify-content-between align-items-start">
            <span>{{ t(key) }}</span>
            <button type="button" class="btn-close" (click)="actionError.set(null)"></button>
          </div>
        }

        @if (channelId() != null) {
          @if (channelError(); as key) {
            <div class="alert alert-warning">{{ t(key) }}</div>
          } @else if (channel(); as ch) {
            <header class="community-channel-header mb-3">
              <div class="d-flex align-items-center flex-wrap gap-2">
                <h2 class="h5 mb-0"># {{ ch.name }}</h2>
                @if (ch.mode === 'news' || ch.mode === 'qa') {
                  <span class="badge bg-info text-dark">{{ t('community.channel.mode.' + ch.mode) }}</span>
                }
                @if (ch.private) {
                  <span class="badge bg-dark community-private-room__badge">🔐 {{ t('community.private_room.badge') }}</span>
                }
                @if (ch.archived) {
                  <span class="badge bg-secondary">{{ t('community.channel.archive') }}</span>
                } @else if (ch.read_only) {
                  <span class="badge bg-secondary">{{ t('community.channel.read_only') }}</span>
                } @else if (ch.private && ch.participants_read_only) {
                  <span class="badge bg-secondary">{{ t('community.private_room.read_only') }}</span>
                }
                <button type="button" class="btn btn-sm btn-outline-secondary ms-auto"
                        [disabled]="subscribeBusy()"
                        [title]="t(ch.subscribed ? 'community.channel.unsubscribe' : 'community.channel.subscribe')"
                        (click)="toggleSubscribe()">
                  {{ ch.subscribed ? '🔔' : '🔕' }}
                  {{ t(ch.subscribed ? 'community.channel.subscribed' : 'community.channel.subscribe') }}
                </button>
                @if (ch.group) {
                  <small class="text-muted" [class.ms-2]="true">{{ ch.group.name }}</small>
                }
              </div>
              @if (ch.description) {
                <p class="text-muted mb-0 mt-1">{{ ch.description }}</p>
              }
              @if (ch.private) {
                <p class="community-private-room__notice small mb-0 mt-1">🔐 {{ t('community.private_room.notice') }}</p>
              }
              @if (ch.mode === 'qa') {
                <div class="btn-group btn-group-sm mt-2" role="group">
                  <button type="button" class="btn"
                    [class.btn-primary]="qaFilter() === 'all'" [class.btn-outline-secondary]="qaFilter() !== 'all'"
                    (click)="selectQaFilter('all')">{{ t('community.qa.filter.all') }}</button>
                  <button type="button" class="btn"
                    [class.btn-primary]="qaFilter() === 'open'" [class.btn-outline-secondary]="qaFilter() !== 'open'"
                    (click)="selectQaFilter('open')">{{ t('community.qa.filter.open') }}</button>
                  <button type="button" class="btn"
                    [class.btn-primary]="qaFilter() === 'solved'" [class.btn-outline-secondary]="qaFilter() !== 'solved'"
                    (click)="selectQaFilter('solved')">{{ t('community.qa.filter.solved') }}</button>
                </div>
              }
            </header>

            @if (ch.legacy) {
              <div class="alert alert-info">{{ t('community.channel.archive_notice') }}</div>
            } @else if (ch.read_only && !ch.permissions.create_thread) {
              <div class="alert alert-light border">{{ t('community.channel.read_only_notice') }}</div>
            }

            <!-- Pinned links: fixed at the top of the channel; managers may edit inline. -->
            @if (activeLinks().length > 0 || canManageLinks()) {
              <section class="community-links mb-3">
                <div class="community-links__header">
                  <span class="community-links__title">📌 {{ t('community.links.title') }}</span>
                  @if (canManageLinks() && linkFormTarget() === null) {
                    <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="linkBusy()" (click)="openLinkForm()">
                      + {{ t('community.links.add') }}
                    </button>
                  }
                </div>

                @if (linkError(); as key) {
                  <div class="alert alert-danger py-2 my-2">{{ t(key) }}</div>
                }

                @if (activeLinks().length === 0 && linkFormTarget() === null) {
                  <p class="small text-muted mb-0">{{ t('community.links.empty') }}</p>
                }

                <ul class="community-links__list">
                  @for (link of activeLinks(); track link.id) {
                    <li class="community-links__item">
                      @if (linkFormTarget() === link.id) {
                        <ng-container *ngTemplateOutlet="linkForm"></ng-container>
                      } @else {
                        <a class="community-links__link" [href]="link.url" target="_blank" rel="noopener noreferrer">
                          <span class="community-links__link-title">{{ link.title }}</span>
                          @if (link.description) {
                            <span class="community-links__link-desc">{{ link.description }}</span>
                          }
                          <span class="community-links__link-url">{{ link.url }}</span>
                        </a>
                        @if (canManageLinks()) {
                          <div class="community-links__actions">
                            <button type="button" class="btn btn-sm btn-link" [disabled]="linkBusy()" (click)="openLinkForm(link)">
                              {{ t('community.links.edit') }}
                            </button>
                            <button type="button" class="btn btn-sm btn-link text-warning" [disabled]="linkBusy()" (click)="setLinkActive(link, false)">
                              {{ t('community.links.deactivate') }}
                            </button>
                            <button type="button" class="btn btn-sm btn-link text-danger" [disabled]="linkBusy()"
                                    (click)="deleteLink(link, t('community.links.delete_confirm', { title: link.title }))">
                              {{ t('community.links.delete') }}
                            </button>
                          </div>
                        }
                      }
                    </li>
                  }

                  <!-- Inactive links: only managers get them, shown muted with a way back. -->
                  @if (canManageLinks()) {
                    @for (link of inactiveLinks(); track link.id) {
                      <li class="community-links__item community-links__item--inactive">
                        @if (linkFormTarget() === link.id) {
                          <ng-container *ngTemplateOutlet="linkForm"></ng-container>
                        } @else {
                          <span class="community-links__link">
                            <span class="community-links__link-title">
                              {{ link.title }}
                              <span class="badge bg-secondary ms-1">{{ t('community.links.inactive') }}</span>
                            </span>
                            <span class="community-links__link-url">{{ link.url }}</span>
                          </span>
                          <div class="community-links__actions">
                            <button type="button" class="btn btn-sm btn-link" [disabled]="linkBusy()" (click)="openLinkForm(link)">
                              {{ t('community.links.edit') }}
                            </button>
                            <button type="button" class="btn btn-sm btn-link text-success" [disabled]="linkBusy()" (click)="setLinkActive(link, true)">
                              {{ t('community.links.reactivate') }}
                            </button>
                            <button type="button" class="btn btn-sm btn-link text-danger" [disabled]="linkBusy()"
                                    (click)="deleteLink(link, t('community.links.delete_confirm', { title: link.title }))">
                              {{ t('community.links.delete') }}
                            </button>
                          </div>
                        }
                      </li>
                    }
                  }

                  @if (linkFormTarget() === 'new') {
                    <li class="community-links__item">
                      <ng-container *ngTemplateOutlet="linkForm"></ng-container>
                    </li>
                  }
                </ul>

                <ng-template #linkForm>
                  <div class="community-links__form row g-2 align-items-end">
                    <div class="col-md-4">
                      <label class="form-label small mb-0">{{ t('community.links.title_label') }}</label>
                      <input type="text" class="form-control form-control-sm" maxlength="80"
                             [(ngModel)]="linkDraft.title" (keyup.enter)="submitLinkForm()" />
                    </div>
                    <div class="col-md-8">
                      <label class="form-label small mb-0">{{ t('community.links.url_label') }}</label>
                      <input type="url" class="form-control form-control-sm" maxlength="1000"
                             [placeholder]="t('community.links.url_placeholder')"
                             [(ngModel)]="linkDraft.url" (keyup.enter)="submitLinkForm()" />
                    </div>
                    <div class="col-12">
                      <label class="form-label small mb-0">{{ t('community.links.description_label') }}</label>
                      <input type="text" class="form-control form-control-sm" maxlength="255"
                             [(ngModel)]="linkDraft.description" (keyup.enter)="submitLinkForm()" />
                    </div>
                    <div class="col-12 d-flex gap-2">
                      <button type="button" class="btn btn-sm btn-primary"
                              [disabled]="linkBusy() || !linkDraftValid()"
                              (click)="submitLinkForm()">
                        {{ t('community.links.save') }}
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelLinkForm()">
                        {{ t('community.threads.cancel') }}
                      </button>
                    </div>
                  </div>
                </ng-template>
              </section>
            }

            @if (topics().length > 0) {
              <nav class="community-list__topics mb-3 d-flex flex-wrap gap-2 align-items-center">
                <small class="text-muted me-1">{{ t('community.channel.topic_filter') }}</small>
                <button type="button" class="btn btn-sm"
                  [class.btn-primary]="activeTopicId() === null"
                  [class.btn-outline-secondary]="activeTopicId() !== null"
                  (click)="selectTopic(null)">
                  {{ t('community.channel.topic_all') }}
                </button>
                @for (topic of topics(); track topic.id) {
                  <button type="button" class="btn btn-sm"
                    [class.btn-primary]="activeTopicId() === topic.id"
                    [class.btn-outline-secondary]="activeTopicId() !== topic.id"
                    (click)="selectTopic(topic.id)">
                    {{ topic.name }}
                    @if (topic.thread_count) {
                      <span class="badge bg-light text-dark ms-1">{{ topic.thread_count }}</span>
                    }
                  </button>
                }
              </nav>
            }
          }
        } @else {
          <header class="community-channel-header mb-3">
            <h2 class="h5 mb-0">{{ t('community.nav.all') }}</h2>
            <p class="text-muted mb-0 mt-1 small">{{ t('community.nav.all_hint') }}</p>
          </header>
        }

        @if (createOpen()) {
          <section class="card mb-3">
            <div class="card-body">
              <h2 class="h5 card-title">{{ t('community.threads.new') }}</h2>
              @if (channelId() == null && postableChannels().length > 0) {
                <div class="mb-2">
                  <label class="form-label">{{ t('community.channel.label') }}</label>
                  <select class="form-select" [(ngModel)]="createChannelId">
                    @for (group of groups(); track group.id) {
                      @if (!group.locked) {
                        <optgroup [label]="group.name">
                          @for (ch of group.channels; track ch.id) {
                            @if (ch.permissions.create_thread) {
                              <option [ngValue]="ch.id"># {{ ch.name }}</option>
                            }
                          }
                        </optgroup>
                      }
                    }
                  </select>
                </div>
              }
              <div class="mb-2">
                <label class="form-label">{{ t('community.threads.title_label') }}</label>
                <input type="text" class="form-control" maxlength="255" [(ngModel)]="titleDraft" />
              </div>
              <div class="mb-2">
                <label class="form-label">{{ t('community.threads.body_label') }}</label>
                <app-community-editor [value]="bodyDraft" (valueChange)="bodyDraft = $event"
                                     [channelId]="createChannelId" [canMention]="createCanMention"
                                     [canUploadImage]="createCanUploadImage" [canUploadFile]="createCanUploadFile"
                                     [canEmbedVideo]="createCanEmbedVideo" (uploadError)="actionError.set($event)"></app-community-editor>
              </div>
              <div class="mt-2">
                <button type="button" class="btn btn-primary me-2"
                        [disabled]="submitting() || createChannelId == null || !titleDraft.trim() || bodyBlank(bodyDraft)"
                        (click)="submitCreate()">
                  {{ t('community.threads.submit') }}
                </button>
                <button type="button" class="btn btn-outline-secondary" (click)="cancelCreate()">
                  {{ t('community.threads.cancel') }}
                </button>
                @if (submitting()) {
                  <span class="text-muted small ms-2">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>{{ t('community.language_check.checking') }}
                  </span>
                }
              </div>
            </div>
          </section>
        }

        @if (loading()) {
          <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
        } @else if (threads().length === 0) {
          <p class="text-muted py-4 text-center">{{ t('community.threads.empty') }}</p>
        } @else {
          <ul class="list-group" [class.community-private-room]="channelIsPrivate()"
              (contextmenu)="channelIsPrivate() && blockContextMenu($event)">
            @for (th of threads(); track th.id; let i = $index) {
              <!-- Pinned threads sort first; on page 1 mark where they end. -->
              @if (pinnedCount() > 0 && i === 0 && th.pinned) {
                <li class="list-group-item community-list__divider">📌 {{ t('community.threads.pinned_section') }}</li>
              }
              @if (pinnedCount() > 0 && !th.pinned && i === pinnedCount()) {
                <li class="list-group-item community-list__divider">{{ t('community.threads.latest_section') }}</li>
              }
              <li class="list-group-item" [class.community-list__item--pinned]="th.pinned"
                  [class.community-private-room]="threadIsPrivate(th)"
                  (contextmenu)="threadIsPrivate(th) && blockContextMenu($event)">
                <div class="d-flex align-items-start gap-2">
                  @if (th.unread) {
                    <span class="community-list__dot community-list__dot--unread" [title]="t('community.unread.thread')"></span>
                  } @else if (th.has_unread_replies) {
                    <span class="community-list__dot community-list__dot--replies" [title]="t('community.unread.replies')"></span>
                  }
                  <div class="community-list__main">
                    <a class="community-list__link d-block text-decoration-none" [routerLink]="threadLink(th.id)">
                      <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                        @if (channelId() == null && th.channel) {
                          <span class="badge bg-info text-dark"># {{ th.channel.name }}</span>
                        }
                        @if (channelId() == null && threadIsPrivate(th)) {
                          <span class="badge bg-dark">🔐 {{ t('community.private_room.badge') }}</span>
                        }
                        @if (th.topic) {
                          <span class="badge bg-light text-dark">{{ th.topic.name }}</span>
                        }
                        @if (th.pinned) {
                          <span class="badge bg-warning text-dark">📌 {{ t('community.threads.pinned') }}</span>
                        }
                        @if (th.locked) {
                          <span class="badge bg-secondary">🔒 {{ t('community.threads.locked') }}</span>
                        }
                        @if (th.channel.mode === 'qa') {
                          @if (th.solved) {
                            <span class="badge bg-success">✓ {{ t('community.qa.solved') }}</span>
                          } @else {
                            <span class="badge bg-light text-dark">{{ t('community.qa.open') }}</span>
                          }
                        }
                        <!-- masked covers members (title blanked); can_moderate covers
                             moderators, who see the thread in full and would otherwise
                             get no hint that the author deleted it. -->
                        @if (th.masked || (th.deleted_at && (th.can_moderate || th.can_admin))) {
                          <span class="badge bg-secondary">🗑 {{ t('community.threads.deleted') }}</span>
                        }
                        @if (th.hidden_at && (th.can_moderate || th.can_admin)) {
                          <span class="badge bg-warning text-dark">{{ t('community.threads.hidden') }}</span>
                        }
                        @if (th.author_hidden || th.user?.community_hidden) {
                          <span class="badge bg-warning text-dark" [title]="t('community.users.hidden_hint')">
                            🚫 {{ t('community.users.hidden') }}
                          </span>
                        }
                        @if (approvalBadge(th); as status) {
                          <span class="badge" [class.bg-warning]="status === 'pending'" [class.text-dark]="status === 'pending'"
                                [class.bg-danger]="status === 'rejected'"
                                [title]="t('community.moderation.approval.' + status + '_hint')">
                            {{ t('community.moderation.approval.' + status) }}
                          </span>
                        }
                      </div>
                      <h3 class="h6 mb-1">
                        {{ th.masked ? t('community.threads.deleted') : th.title }}
                      </h3>
                      @if (!th.masked && th.body) {
                        <p class="text-muted mb-1 community-list__preview">{{ stripHtml(th.body) | slice:0:160 }}</p>
                      }
                    </a>
                    <small class="text-muted">
                      @if (th.user; as author) {
                        <a class="community-list__author" [routerLink]="memberLink(author.id)">{{ author.display_name }}</a>
                      } @else {
                        —
                      }
                      <founding-member-badge compact
                                             [foundingMember]="th.user?.founding_member"
                                             [memberNumber]="th.user?.founding_member_number" />
                      <founding-member-badge compact kind="practitioner"
                                             [foundingMember]="th.user?.founding_practitioner"
                                             [memberNumber]="th.user?.founding_practitioner_number" />
                      <app-community-badges [badges]="th.user?.badges" />
                      · {{ th.created_at | date:'medium' }}
                      @if (th.reply_count) {
                        · {{ th.reply_count }} {{ th.reply_count === 1 ? t('community.threads.reply_label.one') : t('community.threads.reply_label.other') }}
                      }
                      @if (reactionTotal(th.reactions); as total) {
                        · {{ total }} {{ total === 1 ? t('community.reactions.count_label.one') : t('community.reactions.count_label.other') }}
                      }
                    </small>
                  </div>
                </div>
              </li>
            }
          </ul>

          @if (totalPages() > 1) {
            <nav class="mt-3 d-flex gap-2 justify-content-center">
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="page() <= 1" (click)="load(page() - 1)">‹</button>
              <span class="align-self-center small text-muted">{{ page() }} / {{ totalPages() }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="page() >= totalPages()" (click)="load(page() + 1)">›</button>
            </nav>
          }
        }

        <!-- Opt-in card again for phones, where the group column is hidden. -->
        @for (role of optInRoles(); track role.key) {
          <div class="card d-md-none mt-3">
            <div class="card-body p-3">
              <div class="fw-semibold mb-1">{{ role.name }}</div>
              @if (optInDescription(role); as description) {
                <p class="small text-muted mb-2">{{ description }}</p>
              }
              <button type="button" class="btn btn-sm btn-primary"
                      [disabled]="optInPending() === role.key"
                      (click)="optIn(role)">
                {{ t('community.opt_in.join') }}
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Manage channels modal (admin) -->
  @if (manageOpen()) {
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ t('community.manage.title') }}</h5>
            <button type="button" class="btn-close" (click)="closeManage()"></button>
          </div>
          <div class="modal-body">
            @if (manageError(); as key) {
              <div class="alert alert-danger">{{ t(key) }}</div>
            }

            <div class="mb-3">
              <label class="form-label">{{ t('community.manage.group_label') }}</label>
              <select class="form-select" [ngModel]="manageGroupId()" (ngModelChange)="selectManageGroup($event)">
                @for (group of groups(); track group.id) {
                  <option [ngValue]="group.id">{{ group.name }}</option>
                }
              </select>
            </div>

            @if (manageGroup(); as group) {
              @if (manageChannels().length === 0) {
                <p class="text-muted">{{ t('community.manage.channels_empty') }}</p>
              } @else {
                <ul class="list-group mb-3">
                  @for (ch of manageChannels(); track ch.id) {
                    <li class="list-group-item">
                      @if (editingChannelId() === ch.id) {
                        <div class="row g-2 align-items-end">
                          <div class="col-md-5">
                            <label class="form-label small">{{ t('community.manage.name_label') }}</label>
                            <input type="text" class="form-control form-control-sm" maxlength="100"
                              [(ngModel)]="channelEdit.name" (keyup.enter)="saveEditChannel(ch)" />
                          </div>
                          <div class="col-md-3">
                            <label class="form-label small">{{ t('community.manage.mode_label') }}</label>
                            <select class="form-select form-select-sm" [(ngModel)]="channelEdit.mode">
                              @for (mode of modes; track mode) {
                                <option [ngValue]="mode">{{ t('community.channel.mode.' + mode) }}</option>
                              }
                            </select>
                          </div>
                          <div class="col-md-4">
                            <div class="form-check">
                              <input class="form-check-input" type="checkbox" [id]="'ro-' + ch.id" [(ngModel)]="channelEdit.read_only" />
                              <label class="form-check-label small" [for]="'ro-' + ch.id">{{ t('community.manage.read_only_label') }}</label>
                            </div>
                          </div>
                          <div class="col-12">
                            <label class="form-label small">{{ t('community.manage.description_label') }}</label>
                            <input type="text" class="form-control form-control-sm" maxlength="500" [(ngModel)]="channelEdit.description" />
                          </div>
                          <div class="col-12 d-flex gap-2">
                            <button type="button" class="btn btn-sm btn-primary"
                                    [disabled]="manageBusy() || !channelEdit.name.trim()"
                                    (click)="saveEditChannel(ch)">
                              {{ t('community.manage.save') }}
                            </button>
                            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelEditChannel()">
                              {{ t('community.threads.cancel') }}
                            </button>
                          </div>
                        </div>
                      } @else {
                        <div class="d-flex align-items-center flex-wrap gap-2">
                          <span class="flex-grow-1">
                            # {{ ch.name }}
                            <span class="badge bg-info text-dark ms-1">{{ t('community.channel.mode.' + ch.mode) }}</span>
                            @if (ch.read_only) {
                              <span class="badge bg-secondary ms-1">{{ t('community.channel.read_only') }}</span>
                            }
                            @if (ch.archived) {
                              <span class="badge bg-dark ms-1">{{ t('community.manage.archived') }}</span>
                            }
                            @if (ch.system) {
                              <small class="text-muted ms-1">({{ t('community.manage.system_hint') }})</small>
                            }
                            @if (ch.thread_count) {
                              <span class="badge bg-light text-dark ms-1">{{ ch.thread_count }}</span>
                            }
                          </span>
                          <button type="button" class="btn btn-sm btn-outline-secondary" [disabled]="manageBusy()" (click)="startEditChannel(ch)">
                            {{ t('community.manage.rename') }}
                          </button>
                          <button type="button" class="btn btn-sm btn-outline-dark" [disabled]="manageBusy()" (click)="toggleArchived(ch)">
                            {{ ch.archived ? t('community.manage.unarchive') : t('community.manage.archive') }}
                          </button>
                          @if (!ch.system) {
                            <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="manageBusy()"
                                    (click)="deleteChannel(ch, t('community.manage.delete_confirm', { name: ch.name }))">
                              {{ t('community.manage.delete') }}
                            </button>
                          }
                        </div>

                        @if (deletePendingChannel()?.id === ch.id) {
                          <div class="alert alert-warning mt-2 mb-0">
                            <p class="mb-2">{{ t('community.manage.move_required') }}</p>
                            <div class="d-flex flex-wrap gap-2 align-items-center">
                              <label class="small mb-0">{{ t('community.manage.move_target_label') }}</label>
                              <select class="form-select form-select-sm w-auto" [(ngModel)]="deleteMoveTargetId">
                                @for (target of deleteMoveTargets(); track target.id) {
                                  <option [ngValue]="target.id"># {{ target.name }}</option>
                                }
                              </select>
                              <button type="button" class="btn btn-sm btn-danger"
                                      [disabled]="manageBusy() || deleteMoveTargetId == null"
                                      (click)="confirmDeleteWithMove()">
                                {{ t('community.manage.move_and_delete') }}
                              </button>
                              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancelDeleteWithMove()">
                                {{ t('community.threads.cancel') }}
                              </button>
                            </div>
                          </div>
                        }
                      }
                    </li>
                  }
                </ul>
              }

              <h6>{{ t('community.manage.add') }}</h6>
              <div class="row g-2 align-items-end">
                <div class="col-md-5">
                  <label class="form-label small">{{ t('community.manage.name_label') }}</label>
                  <input type="text" class="form-control form-control-sm" maxlength="100"
                    [placeholder]="t('community.manage.name_placeholder')"
                    [(ngModel)]="newChannel.name" (keyup.enter)="addChannel()" />
                </div>
                <div class="col-md-3">
                  <label class="form-label small">{{ t('community.manage.mode_label') }}</label>
                  <select class="form-select form-select-sm" [(ngModel)]="newChannel.mode">
                    @for (mode of modes; track mode) {
                      <option [ngValue]="mode">{{ t('community.channel.mode.' + mode) }}</option>
                    }
                  </select>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="newChannelReadOnly" [(ngModel)]="newChannel.read_only" />
                    <label class="form-check-label small" for="newChannelReadOnly">{{ t('community.manage.read_only_label') }}</label>
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label small">{{ t('community.manage.description_label') }}</label>
                  <input type="text" class="form-control form-control-sm" maxlength="500" [(ngModel)]="newChannel.description" />
                </div>
                <div class="col-12">
                  <button type="button" class="btn btn-sm btn-primary"
                          [disabled]="manageBusy() || !newChannel.name.trim()"
                          (click)="addChannel()">
                    {{ t('community.manage.add') }}
                  </button>
                </div>
              </div>
            }

            <!-- Muted users: the only place to un-mute someone who has no
                 visible content left to click on. -->
            @if (hiddenUsers().length > 0) {
              <hr class="my-4" />
              <h6>🚫 {{ t('community.users.hidden_title') }}</h6>
              <p class="text-muted small">{{ t('community.users.hidden_hint') }}</p>
              <ul class="list-group">
                @for (u of hiddenUsers(); track u.id) {
                  <li class="list-group-item d-flex align-items-center gap-2">
                    <span class="flex-grow-1">{{ u.display_name }}</span>
                    <button type="button" class="btn btn-sm btn-outline-success" (click)="unhideUser(u)">
                      {{ t('community.users.unhide') }}
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" (click)="closeManage()">
              {{ t('community.threads.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show"></div>
  }

  <!-- Un-mute: the reason goes into the moderation protocol -->
  @if (unhideTarget(); as user) {
    <app-community-reason-modal titleKey="community.users.unhide_title"
                                [titleParams]="{ name: user.display_name }"
                                confirmKey="community.users.unhide"
                                confirmClass="btn-success"
                                [busy]="unhideBusy()"
                                [error]="unhideError()"
                                (confirmed)="confirmUnhide($event)"
                                (cancelled)="unhideTarget.set(null)" />
  }

  <!-- Renders itself only when a save ran into a confident language mismatch (§7.3) -->
  <app-community-language-confirm-modal />
</div>
